import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { OrderDetailDialogProps } from "./OrderDetailDialog.connector";
import type {
  OrderReplacementDialogProps,
  OrderReplacementIdentity,
  OrderReplacementSubmittedResult,
} from "./OrderReplacementDialog.connector";
import type { OrdersWidgetCancellationFeedback } from "./OrdersCancellation.pres";
import type { OrdersWidgetRow } from "./OrdersWidget.types";
import { ORDERS_UNAVAILABLE_VALUE } from "./OrdersWidget.utils";

interface SelectedOrderIdentity {
  accountID: string;
  product: "live" | "paper";
  orderID: string;
}

interface SelectedFocusOrigin {
  element: HTMLElement;
  ariaLabel: string | null;
}

export const useOrdersWidgetDialogsState = ({
  accountID,
  product,
}: {
  accountID?: string;
  product?: "live" | "paper";
}) => {
  const [replacementFeedback, setReplacementFeedback] = useState<string>();
  const [selectedOrder, setSelectedOrder] = useState<SelectedOrderIdentity>();
  const [selectedReplacement, setSelectedReplacement] =
    useState<OrderReplacementIdentity>();
  const [replacementPendingOrderID, setReplacementPendingOrderID] =
    useState<string>();
  const selectedDetailOriginRef = useRef<SelectedFocusOrigin | null>(null);
  const selectedReplacementOriginRef = useRef<SelectedFocusOrigin | null>(null);
  const resultsRegionRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: accountID/product are reset triggers; the body deliberately does not capture stale identity values
  useEffect(() => {
    setReplacementFeedback(undefined);
    setSelectedReplacement(undefined);
    setReplacementPendingOrderID(undefined);
    selectedReplacementOriginRef.current = null;
    selectedDetailOriginRef.current = null;
  }, [accountID, product]);

  useEffect(() => {
    if (
      selectedOrder &&
      (selectedOrder.accountID !== accountID ||
        selectedOrder.product !== product)
    ) {
      setSelectedOrder(undefined);
      selectedDetailOriginRef.current = null;
    }
  }, [accountID, product, selectedOrder]);

  useEffect(() => {
    if (
      selectedReplacement &&
      (selectedReplacement.accountID !== accountID ||
        selectedReplacement.product !== product)
    ) {
      setSelectedReplacement(undefined);
      setReplacementPendingOrderID(undefined);
      selectedReplacementOriginRef.current = null;
    }
  }, [accountID, product, selectedReplacement]);

  return {
    replacementFeedback,
    setReplacementFeedback,
    selectedOrder,
    selectedReplacement,
    replacementPendingOrderID,
    selectedDetailOriginRef,
    selectedReplacementOriginRef,
    resultsRegionRef,
    setSelectedOrder,
    setSelectedReplacement,
    setReplacementPendingOrderID,
  };
};

export const useOrdersWidgetDialogsController = ({
  state,
  rows,
  allowCancel,
  accountID,
  product,
  setCancellationFeedback,
  cancellingIdSetRef,
}: {
  state: ReturnType<typeof useOrdersWidgetDialogsState>;
  rows: OrdersWidgetRow[];
  allowCancel: boolean;
  accountID?: string;
  product?: "live" | "paper";
  setCancellationFeedback: (
    feedback: OrdersWidgetCancellationFeedback | undefined,
  ) => void;
  cancellingIdSetRef: MutableRefObject<Set<string>>;
}) => {
  const {
    replacementFeedback,
    setReplacementFeedback,
    selectedOrder,
    selectedReplacement,
    replacementPendingOrderID,
    selectedDetailOriginRef,
    selectedReplacementOriginRef,
    resultsRegionRef,
    setSelectedOrder,
    setSelectedReplacement,
    setReplacementPendingOrderID,
  } = state;

  const editableIdSet = useMemo(
    () =>
      new Set(
        rows.filter((row) => row.canEdit && !row.isLegRow).map((row) => row.id),
      ),
    [rows],
  );
  const editableIdSetRef = useRef(editableIdSet);
  editableIdSetRef.current = editableIdSet;
  const replacementPendingOrderIDRef = useRef(replacementPendingOrderID);
  replacementPendingOrderIDRef.current = replacementPendingOrderID;
  const selectedReplacementRef = useRef(selectedReplacement);
  selectedReplacementRef.current = selectedReplacement;

  const handleEdit = useCallback(
    (orderID: string, origin: HTMLButtonElement) => {
      if (
        !allowCancel ||
        !accountID ||
        !product ||
        !editableIdSetRef.current.has(orderID) ||
        cancellingIdSetRef.current.has(orderID) ||
        replacementPendingOrderIDRef.current === orderID
      ) {
        return;
      }

      setCancellationFeedback(undefined);
      setReplacementFeedback(undefined);
      selectedReplacementOriginRef.current = {
        element: origin,
        ariaLabel: origin.getAttribute("aria-label"),
      };
      setSelectedReplacement({ accountID, product, orderID });
    },
    [
      accountID,
      allowCancel,
      cancellingIdSetRef,
      product,
      setCancellationFeedback,
      setReplacementFeedback,
      setSelectedReplacement,
      selectedReplacementOriginRef,
    ],
  );

  const handleReplacementPendingChange = useCallback(
    (orderID: string, pending: boolean) => {
      const activeReplacement = selectedReplacementRef.current;
      if (
        !activeReplacement ||
        activeReplacement.accountID !== accountID ||
        activeReplacement.product !== product ||
        activeReplacement.orderID !== orderID
      ) {
        return;
      }
      setReplacementPendingOrderID((current) =>
        pending ? orderID : current === orderID ? undefined : current,
      );
    },
    [accountID, product, setReplacementPendingOrderID],
  );

  const handleReplacementSubmitted = useCallback(
    ({ original, replacementOrder }: OrderReplacementSubmittedResult) => {
      const activeReplacement = selectedReplacementRef.current;
      if (
        original.accountID !== accountID ||
        original.product !== product ||
        activeReplacement?.accountID !== original.accountID ||
        activeReplacement.product !== original.product ||
        activeReplacement.orderID !== original.orderID
      ) {
        return;
      }

      const replacementOrderID = replacementOrder?.id?.trim();
      setReplacementPendingOrderID(undefined);
      setSelectedReplacement(undefined);
      if (!replacementOrderID) {
        setReplacementFeedback(
          "Replacement submitted, but the new order ID was unavailable.",
        );
        return;
      }

      setReplacementFeedback("Replacement submitted.");
    },
    [
      accountID,
      product,
      setReplacementFeedback,
      setReplacementPendingOrderID,
      setSelectedReplacement,
    ],
  );

  const handleReplacementOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setSelectedReplacement(undefined);
        setReplacementPendingOrderID(undefined);
      }
    },
    [setReplacementPendingOrderID, setSelectedReplacement],
  );

  const handleReplacementCloseAutoFocus = useCallback(
    (event: Event) => {
      const origin = selectedReplacementOriginRef.current;
      selectedReplacementOriginRef.current = null;
      const originStillMatches =
        origin?.element.isConnected === true &&
        origin.element.getAttribute("aria-label") === origin.ariaLabel;
      const focusTarget =
        originStillMatches && origin
          ? origin.element
          : resultsRegionRef.current;
      if (focusTarget?.isConnected) {
        event.preventDefault();
        focusTarget.focus();
      }
    },
    [resultsRegionRef, selectedReplacementOriginRef],
  );

  const handleRowActivate = useCallback(
    (row: OrdersWidgetRow, rowElement: HTMLTableRowElement) => {
      if (
        !accountID ||
        !product ||
        row.parentOrderId === ORDERS_UNAVAILABLE_VALUE
      ) {
        return;
      }
      selectedDetailOriginRef.current = {
        element: rowElement,
        ariaLabel: rowElement.getAttribute("aria-label"),
      };
      setSelectedOrder({
        accountID,
        product,
        orderID: row.parentOrderId,
      });
    },
    [accountID, product, selectedDetailOriginRef, setSelectedOrder],
  );

  const handleDetailOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setSelectedOrder(undefined);
      }
    },
    [setSelectedOrder],
  );

  const handleDetailCloseAutoFocus = useCallback(
    (event: Event) => {
      const origin = selectedDetailOriginRef.current;
      selectedDetailOriginRef.current = null;
      const originStillMatches =
        origin?.element.isConnected === true &&
        origin.element.getAttribute("aria-label") === origin.ariaLabel;
      const focusTarget =
        originStillMatches && origin
          ? origin.element
          : resultsRegionRef.current;
      if (focusTarget?.isConnected) {
        event.preventDefault();
        focusTarget.focus();
      }
    },
    [resultsRegionRef, selectedDetailOriginRef],
  );

  const isDetailOpen =
    selectedOrder !== undefined &&
    selectedOrder.accountID === accountID &&
    selectedOrder.product === product;
  const isReplacementOpen =
    selectedReplacement !== undefined &&
    selectedReplacement.accountID === accountID &&
    selectedReplacement.product === product;

  const detailDialogProps: OrderDetailDialogProps = {
    open: isDetailOpen,
    accountID,
    product,
    orderID: isDetailOpen ? selectedOrder.orderID : undefined,
    onOpenChange: handleDetailOpenChange,
    onCloseAutoFocus: handleDetailCloseAutoFocus,
  };

  const replacementDialogProps: OrderReplacementDialogProps = {
    open: isReplacementOpen,
    variant: "inline",
    accountID,
    product,
    orderID: isReplacementOpen ? selectedReplacement?.orderID : undefined,
    onOpenChange: handleReplacementOpenChange,
    onSubmitted: handleReplacementSubmitted,
    onMutationPendingChange: handleReplacementPendingChange,
    onCloseAutoFocus: handleReplacementCloseAutoFocus,
  };

  return {
    handleEdit,
    handleRowActivate,
    replacementFeedback,
    setReplacementFeedback,
    resultsRegionRef,
    selectedOrderId: isDetailOpen
      ? selectedOrder.orderID
      : isReplacementOpen
        ? selectedReplacement.orderID
        : undefined,
    detailDialogProps,
    replacementDialogProps,
  };
};
